import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Table, Button, Form, Card } from "react-bootstrap";
import { LinearProgress } from "@material-ui/core";
import AppConfig from "../../config/AppConfig";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend as ChartLegend
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  ChartLegend
);

// Beautiful SVG Icons
const DownloadIcon = () => (
  <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" className="mr-2" style={{ display: "inline-block", verticalAlign: "middle" }}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
  </svg>
);

const SortIcon = ({ active, desc }) => (
  <svg
    width="12"
    height="12"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    viewBox="0 0 24 24"
    className="ml-1"
    style={{
      display: "inline-block",
      verticalAlign: "middle",
      color: active ? "#ffffff" : "rgba(255, 255, 255, 0.4)",
      transform: desc ? "rotate(180deg)" : "none",
      transition: "transform 0.15s ease"
    }}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
  </svg>
);

export default function Rekap() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [sortField, setSortField] = useState("nm_lemb");
  const [sortOrder, setSortOrder] = useState("asc"); // 'asc' or 'desc'

  const currentYear = new Date().getFullYear();
  const years = useMemo(() => {
    const list = [];
    for (let y = 2021; y <= currentYear; y++) {
      list.push(y);
    }
    return list;
  }, [currentYear]);

  useEffect(() => {
    setLoading(true);
    const userdata = JSON.parse(localStorage.getItem("sessiondata")) || {};
    const id_bp = userdata.i_idbp || "0";
    
    axios
      .get(AppConfig.ListRekapitulasi + "?id_bp=" + id_bp + "&tahun_siklus=" + selectedYear)
      .then((res) => {
        const rawData = res.data?.result?.data || [];
        const processed = rawData.map((item) => {
          const total = item.jumlah_pt || 0;
          return {
            ...item,
            persentase_pt_mengisi: total > 0 ? Math.round((item.sudah_isi / total) * 100) : 0,
            persentase_pt_mengisi_lengkap: total > 0 ? Math.round((item.spmi_lengkap / total) * 100) : 0,
            persentase_pt_terverifikasi_lengkap: total > 0 ? Math.round((item.spmi_lengkap_terverifikasi / total) * 100) : 0,
          };
        });
        setData(processed);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching rekap data:", err);
        setLoading(false);
      });
  }, [selectedYear]);

  // Compute overall summary statistics
  const summary = useMemo(() => {
    const totalLembaga = data.length;
    const totalPT = data.reduce((acc, curr) => acc + (curr.jumlah_pt || 0), 0);
    const totalBelumMengisi = data.reduce((acc, curr) => acc + (curr.belum_isi || 0), 0);
    const avgMengisi = totalPT > 0 ? Math.round(((totalPT - totalBelumMengisi) / totalPT) * 100) : 0;
    
    return {
      totalLembaga,
      totalPT,
      totalBelumMengisi,
      avgMengisi
    };
  }, [data]);

  // Handle Excel Export
  const exportExcel = () => {
    var title = `<h3>Rekap Pelaporan SPMI - Tahun ${selectedYear}</h3>`;
    var tab_text =
      title +
      "<table border='2'><thead>" +
      "<tr style='background-color:#3047ad; color:white; font-weight:bold;'>" +
      "<th rowspan='2'>No</th>" +
      "<th rowspan='2'>Lembaga</th>" +
      "<th rowspan='2'>Total PT</th>" +
      "<th rowspan='2'>Belum Mengisi</th>" +
      "<th colspan='3'>Pengaturan Standar</th>" +
      "<th colspan='2'>Evaluasi</th>" +
      "<th colspan='2'>Pengendalian</th>" +
      "<th colspan='2'>Peningkatan</th>" +
      "<th rowspan='2'>Persentase PT Mengisi</th>" +
      "<th rowspan='2'>Persentase PT Mengisi Lengkap</th>" +
      "<th rowspan='2'>Persentase PT Terverifikasi Lengkap</th>" +
      "<th rowspan='2'>PT Terakreditasi</th>" +
      "<th rowspan='2'>PT Belum Terakreditasi</th>" +
      "</tr>" +
      "<tr style='background-color:#3047ad; color:white; font-weight:bold;'>" +
      "<th>Pengaturan Terisi Lengkap</th>" +
      "<th>Pengaturan Terisi Belum Lengkap</th>" +
      "<th>Pengaturan Terverifikasi</th>" +
      "<th>Evaluasi Terisi Lengkap</th>" +
      "<th>Evaluasi Terverifikasi</th>" +
      "<th>Pengendalian Terisi</th>" +
      "<th>Pengendalian Terverifikasi</th>" +
      "<th>Peningkatan Terisi</th>" +
      "<th>Peningkatan Terverifikasi</th>" +
      "</tr>" +
      "</thead><tbody>";
    
    filteredAndSortedData.forEach((list, i) => {
      var no = i + 1;
      tab_text += "<tr>";
      tab_text += "<td align='center'>" + no + "</td>";
      tab_text += "<td align='left'>" + list.nm_lemb + "</td>";
      tab_text += "<td align='center'>" + (list.jumlah_pt || 0) + "</td>";
      tab_text += "<td align='center'>" + (list.belum_isi || 0) + "</td>";
      tab_text += "<td align='center'>" + (list.pengaturan_standar_lengkap || 0) + "</td>";
      tab_text += "<td align='center'>" + (list.pengaturan_standar_belum_lengkap || 0) + "</td>";
      tab_text += "<td align='center'>" + (list.pengaturan_standar_lengkap_terverifikasi || 0) + "</td>";
      tab_text += "<td align='center'>" + (list.evaluasi_lengkap || 0) + "</td>";
      tab_text += "<td align='center'>" + (list.evaluasi_lengkap_terverifikasi || 0) + "</td>";
      tab_text += "<td align='center'>" + (list.pengendalian_terisi || 0) + "</td>";
      tab_text += "<td align='center'>" + (list.pengendalian_terverifikasi || 0) + "</td>";
      tab_text += "<td align='center'>" + (list.peningkatan_terisi || 0) + "</td>";
      tab_text += "<td align='center'>" + (list.peningkatan_terisi_terverifikasi || 0) + "</td>";
      tab_text += "<td align='center'>" + list.persentase_pt_mengisi + "%</td>";
      tab_text += "<td align='center'>" + list.persentase_pt_mengisi_lengkap + "%</td>";
      tab_text += "<td align='center'>" + list.persentase_pt_terverifikasi_lengkap + "%</td>";
      tab_text += "<td align='center'>" + (list.pt_akreditasi || 0) + "</td>";
      tab_text += "<td align='center'>" + (list.pt_tanpa_akreditasi || 0) + "</td>";
      tab_text += "</tr>";
    });

    tab_text = tab_text + "</tbody></table>";
    tab_text = tab_text.replace(/<A[^>]*>|<\/A>/g, "");
    tab_text = tab_text.replace(/<img[^>]*>/gi, "");
    tab_text = tab_text.replace(/<input[^>]*>|<\/input>/gi, "");

    var link = window.document.createElement("a");
    link.setAttribute(
      "href",
      "data:text/xls;charset=utf-8,%EF%BB%BF" + encodeURIComponent(tab_text)
    );
    link.setAttribute(
      "download",
      `rekap_pelaporan_spmi_${selectedYear}.xls`
    );
    link.click();
  };

  // Handle table column header click for sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("desc"); // Default to desc when changing field
    }
  };

  // Filter and Sort Data
  const filteredAndSortedData = useMemo(() => {
    let result = [...data];

    // Apply sorting
    result.sort((a, b) => {
      let valA = a[sortField];
      let valB = b[sortField];

      // Handle undefined/null
      if (valA === undefined || valA === null) valA = 0;
      if (valB === undefined || valB === null) valB = 0;

      // Handle string type sorting
      if (typeof valA === "string") {
        return sortOrder === "asc"
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA);
      }

      // Handle numeric type sorting
      return sortOrder === "asc" ? valA - valB : valB - valA;
    });

    return result;
  }, [data, sortField, sortOrder]);

  const headers = [
    { label: "No", field: "", rowSpan: 2 },
    { label: "Lembaga", field: "nm_lemb", rowSpan: 2 },
    { label: "Total PT", field: "jumlah_pt", rowSpan: 2 },
    { label: "Belum Mengisi", field: "belum_isi", rowSpan: 2 },
    { label: "Pengaturan Standar", field: "", colSpan: 3 },
    { label: "Evaluasi", field: "", colSpan: 2 },
    { label: "Pengendalian", field: "", colSpan: 2 },
    { label: "Peningkatan", field: "", colSpan: 2 },
    { label: "Persentase PT Mengisi", field: "persentase_pt_mengisi", rowSpan: 2 },
    { label: "Persentase PT Mengisi Lengkap", field: "persentase_pt_mengisi_lengkap", rowSpan: 2 },
    { label: "Persentase PT Terverifikasi Lengkap", field: "persentase_pt_terverifikasi_lengkap", rowSpan: 2 },
    { label: "PT Terakreditasi", field: "pt_akreditasi", rowSpan: 2 },
    { label: "PT Belum Terakreditasi", field: "pt_tanpa_akreditasi", rowSpan: 2 }
  ];

  const subHeaders = [
    { label: "Pengaturan Terisi Lengkap", field: "pengaturan_standar_lengkap" },
    { label: "Pengaturan Terisi Belum Lengkap", field: "pengaturan_standar_belum_lengkap" },
    { label: "Pengaturan Terverifikasi", field: "pengaturan_standar_lengkap_terverifikasi" },
    { label: "Evaluasi Terisi Lengkap", field: "evaluasi_lengkap" },
    { label: "Evaluasi Terverifikasi", field: "evaluasi_lengkap_terverifikasi" },
    { label: "Pengendalian Terisi", field: "pengendalian_terisi" },
    { label: "Pengendalian Terverifikasi", field: "pengendalian_terverifikasi" },
    { label: "Peningkatan Terisi", field: "peningkatan_terisi" },
    { label: "Peningkatan Terverifikasi", field: "peningkatan_terisi_terverifikasi" }
  ];

  return (
    <div className="p-4" style={{ fontFamily: "Inter, system-ui, sans-serif" }}>
      {/* Header Banner */}
      <div 
        style={{
          background: "linear-gradient(135deg, #0051CD 0%, #0283B1 100%)",
          color: "white",
          padding: "24px 32px",
          borderRadius: "5px",
          marginBottom: "24px",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)"
        }}
      >
        <h2 style={{ fontWeight: 700, margin: 0, letterSpacing: "-0.02em" }}>Rekap Pelaporan SPMI</h2>
        <p style={{ color: "#ffffffff", margin: "4px 0 0 0", fontSize: "14px" }}>
          Informasi ringkasan implementasi SPMI di Perguruan Tinggi.
        </p>
      </div>

      {/* Metrics Summary Cards */}
      <div className="row mb-4">
        {[
          { title: "Total Perguruan Tinggi", val: summary.totalPT, desc: "Seluruh PT Terdaftar", gradient: "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)" },
          { title: "PT Belum Mengisi", val: summary.totalBelumMengisi, desc: "PT yang Belum Mengisi Data", gradient: "linear-gradient(135deg, #f43f5e 0%, #e11d48 100%)" },
          { title: "Persentase Mengisi Nasional", val: `${summary.avgMengisi}%`, desc: "Rata-rata Pengisian Nasional", gradient: "linear-gradient(135deg, #10b981 0%, #059669 100%)" }
        ].map((card, idx) => (
          <div className="col-lg-4 col-md-6 mb-3" key={idx}>
            <div 
              style={{
                background: card.gradient,
                color: "white",
                padding: "20px 24px",
                borderRadius: "5px",
                boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                minHeight: "130px"
              }}
            >
              <span style={{ fontSize: "14px", fontWeight: 500, opacity: 0.9 }}>{card.title}</span>
              <h3 style={{ fontSize: "28px", fontWeight: 800, margin: "8px 0 4px 0" }}>{card.val}</h3>
              <span style={{ fontSize: "12px", opacity: 0.8 }}>{card.desc}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Main Card */}
      <Card style={{ border: "none", borderRadius: "5px", boxShadow: "0 4px 25px rgba(0,0,0,0.05)", overflow: "hidden" }}>
        <Card.Body style={{ padding: "24px" }}>
          
          {/* Controls Panel */}
          <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center mb-4 gap-3">
            <div style={{ width: "100%", maxWidth: "240px" }} className="d-flex align-items-center">
              <Form.Label className="mr-3 mb-0" style={{ fontWeight: 600, fontSize: "14px", color: "#64748b", whiteSpace: "nowrap" }}>Pilih Tahun:</Form.Label>
              <Form.Control
                as="select"
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
                style={{
                  borderRadius: "5px",
                  border: "1px solid #e2e8f0",
                  height: "42px",
                  fontSize: "14px",
                  boxShadow: "none",
                  fontWeight: 600,
                  color: "#1e293b"
                }}
              >
                {years.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </Form.Control>
            </div>
            
            <div className="d-flex gap-2 w-100 w-sm-auto justify-content-end">
              <Button 
                onClick={exportExcel} 
                variant="success" 
                className="d-flex align-items-center justify-content-center"
                style={{
                  borderRadius: "5px",
                  padding: "0 20px",
                  height: "42px",
                  fontSize: "14px",
                  fontWeight: 600,
                  border: "none",
                  boxShadow: "0 4px 10px rgba(16, 185, 129, 0.2)"
                }}
              >
                <DownloadIcon />
                Export Excel
              </Button>
            </div>
          </div>

          {/* Table Container */}
          <div className="table-responsive">
            <Table 
              hover 
              style={{ 
                margin: 0, 
                borderCollapse: "separate", 
                borderSpacing: "0 2px",
                width: "100%",
                minWidth: "1200px"
              }}
            >
              <thead 
                className="text-white"
                style={{ backgroundColor: "#3047AD" }}
              >
                <tr style={{ background: "transparent" }}>
                  {headers.map((col, idx) => (
                    <th 
                      key={idx}
                      rowSpan={col.rowSpan}
                      colSpan={col.colSpan}
                      onClick={() => col.field && handleSort(col.field)}
                      style={{ 
                        color: "#ffffff",
                        fontSize: "12px",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        border: "none",
                        padding: "12px 16px",
                        cursor: col.field ? "pointer" : "default",
                        userSelect: "none",
                        textAlign: "center",
                        verticalAlign: "middle"
                      }}
                    >
                      <div className="d-flex align-items-center justify-content-center">
                        {col.label}
                        {col.field && <SortIcon active={sortField === col.field} desc={sortOrder === "desc"} />}
                      </div>
                    </th>
                  ))}
                </tr>
                <tr style={{ background: "transparent" }}>
                  {subHeaders.map((col, idx) => (
                    <th 
                      key={idx}
                      onClick={() => col.field && handleSort(col.field)}
                      style={{ 
                        color: "#ffffff",
                        fontSize: "12px",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        border: "none",
                        padding: "12px 16px",
                        cursor: col.field ? "pointer" : "default",
                        userSelect: "none",
                        textAlign: "center",
                        verticalAlign: "middle"
                      }}
                    >
                      <div className="d-flex align-items-center justify-content-center">
                        {col.label}
                        {col.field && <SortIcon active={sortField === col.field} desc={sortOrder === "desc"} />}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="18" style={{ padding: "40px", border: "none" }}>
                      <LinearProgress color="secondary" variant="query" />
                    </td>
                  </tr>
                ) : filteredAndSortedData.length === 0 ? (
                  <tr>
                    <td colSpan="18" className="text-center text-muted" style={{ padding: "40px 16px", backgroundColor: "#f8fafc", borderRadius: "12px", border: "none" }}>
                      Data tidak ditemukan
                    </td>
                  </tr>
                ) : (
                  filteredAndSortedData.map((item, idx) => {
                    const statusColor = item.persentase_pt_mengisi >= 75 ? "#10b981" : item.persentase_pt_mengisi >= 40 ? "#f59e0b" : "#ef4444";
                    
                    return (
                      <tr 
                        key={item.id_lemb_non_sp || idx}
                        style={{
                          backgroundColor: "#fff",
                          boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
                          borderRadius: "5px",
                          transition: "all 0.2s ease"
                        }}
                        className="align-middle border-0"
                      >
                        {/* No */}
                        <td className="text-center text-muted" style={{ padding: "16px", fontWeight: 500, borderRadius: "5px 0 0 5px", borderTop: "1px solid #f1f5f9", borderBottom: "1px solid #f1f5f9", borderLeft: "1px solid #f1f5f9", textAlign: "center", verticalAlign: "middle" }}>
                          {idx + 1}
                        </td>

                        {/* Lembaga */}
                        <td style={{ padding: "16px", fontWeight: 600, color: "#1e293b", borderTop: "1px solid #f1f5f9", borderBottom: "1px solid #f1f5f9", verticalAlign: "middle" }}>
                          <Link to={"/detail-lembaga/" + item.id_lemb_non_sp} style={{ color: "#3b82f6", textDecoration: "none" }}>
                            {item.nm_lemb}
                          </Link>
                        </td>

                        {/* Total PT */}
                        <td className="text-center" style={{ padding: "16px", fontWeight: 600, color: "#475569", borderTop: "1px solid #f1f5f9", borderBottom: "1px solid #f1f5f9", textAlign: "center", verticalAlign: "middle" }}>
                          {item.jumlah_pt || 0}
                        </td>

                        {/* Belum Mengisi */}
                        <td className="text-center" style={{ padding: "16px", borderTop: "1px solid #f1f5f9", borderBottom: "1px solid #f1f5f9", textAlign: "center", verticalAlign: "middle" }}>
                          <Link 
                            to={`daftar-pt?lembaga=${item.nm_lemb}&tipe=belumisi`} 
                            style={{ 
                              color: "#ef4444", 
                              fontWeight: 600, 
                              textDecoration: "none",
                              backgroundColor: "#fee2e2",
                              padding: "4px 10px",
                              borderRadius: "6px",
                              display: "inline-block"
                            }}
                          >
                            {item.belum_isi || 0}
                          </Link>
                        </td>

                        {/* Pengaturan Terisi Lengkap */}
                        <td className="text-center" style={{ padding: "16px", borderTop: "1px solid #f1f5f9", borderBottom: "1px solid #f1f5f9", textAlign: "center", verticalAlign: "middle" }}>
                          <Link to={`daftar-pt?lembaga=${item.nm_lemb}&tipe=pengaturanlengkap`} style={{ color: "#3b82f6", fontWeight: 500, textDecoration: "none" }}>
                            {item.pengaturan_standar_lengkap || 0}
                          </Link>
                        </td>

                        {/* Pengaturan Terisi Belum Lengkap */}
                        <td className="text-center" style={{ padding: "16px", borderTop: "1px solid #f1f5f9", borderBottom: "1px solid #f1f5f9", textAlign: "center", verticalAlign: "middle" }}>
                          <Link to={`daftar-pt?lembaga=${item.nm_lemb}&tipe=pengaturanbelumlengkap`} style={{ color: "#3b82f6", fontWeight: 500, textDecoration: "none" }}>
                            {item.pengaturan_standar_belum_lengkap || 0}
                          </Link>
                        </td>

                        {/* Pengaturan Terverifikasi */}
                        <td className="text-center" style={{ padding: "16px", borderTop: "1px solid #f1f5f9", borderBottom: "1px solid #f1f5f9", textAlign: "center", verticalAlign: "middle" }}>
                          <Link to={`daftar-pt?lembaga=${item.nm_lemb}&tipe=pengaturanlengkapterverifikasi`} style={{ color: "#3b82f6", fontWeight: 500, textDecoration: "none" }}>
                            {item.pengaturan_standar_lengkap_terverifikasi || 0}
                          </Link>
                        </td>

                        {/* Evaluasi Terisi Lengkap */}
                        <td className="text-center" style={{ padding: "16px", borderTop: "1px solid #f1f5f9", borderBottom: "1px solid #f1f5f9", textAlign: "center", verticalAlign: "middle" }}>
                          <Link to={`daftar-pt?lembaga=${item.nm_lemb}&tipe=evaluasilengkap`} style={{ color: "#3b82f6", fontWeight: 500, textDecoration: "none" }}>
                            {item.evaluasi_lengkap || 0}
                          </Link>
                        </td>

                        {/* Evaluasi Terverifikasi */}
                        <td className="text-center" style={{ padding: "16px", borderTop: "1px solid #f1f5f9", borderBottom: "1px solid #f1f5f9", textAlign: "center", verticalAlign: "middle" }}>
                          <Link to={`daftar-pt?lembaga=${item.nm_lemb}&tipe=evaluasilengkapterverifikasi`} style={{ color: "#3b82f6", fontWeight: 500, textDecoration: "none" }}>
                            {item.evaluasi_lengkap_terverifikasi || 0}
                          </Link>
                        </td>

                        {/* Pengendalian Terisi */}
                        <td className="text-center" style={{ padding: "16px", borderTop: "1px solid #f1f5f9", borderBottom: "1px solid #f1f5f9", textAlign: "center", verticalAlign: "middle" }}>
                          <Link to={`daftar-pt?lembaga=${item.nm_lemb}&tipe=pengendalianterisi`} style={{ color: "#3b82f6", fontWeight: 500, textDecoration: "none" }}>
                            {item.pengendalian_terisi || 0}
                          </Link>
                        </td>

                        {/* Pengendalian Terverifikasi */}
                        <td className="text-center" style={{ padding: "16px", borderTop: "1px solid #f1f5f9", borderBottom: "1px solid #f1f5f9", textAlign: "center", verticalAlign: "middle" }}>
                          <Link to={`daftar-pt?lembaga=${item.nm_lemb}&tipe=pengendalianterverifikasi`} style={{ color: "#3b82f6", fontWeight: 500, textDecoration: "none" }}>
                            {item.pengendalian_terverifikasi || 0}
                          </Link>
                        </td>

                        {/* Peningkatan Terisi */}
                        <td className="text-center" style={{ padding: "16px", borderTop: "1px solid #f1f5f9", borderBottom: "1px solid #f1f5f9", textAlign: "center", verticalAlign: "middle" }}>
                          <Link to={`daftar-pt?lembaga=${item.nm_lemb}&tipe=peningkatanterisi`} style={{ color: "#3b82f6", fontWeight: 500, textDecoration: "none" }}>
                            {item.peningkatan_terisi || 0}
                          </Link>
                        </td>

                        {/* Peningkatan Terverifikasi */}
                        <td className="text-center" style={{ padding: "16px", borderTop: "1px solid #f1f5f9", borderBottom: "1px solid #f1f5f9", textAlign: "center", verticalAlign: "middle" }}>
                          <Link to={`daftar-pt?lembaga=${item.nm_lemb}&tipe=peningkatanterverifikasi`} style={{ color: "#3b82f6", fontWeight: 500, textDecoration: "none" }}>
                            {item.peningkatan_terisi_terverifikasi || 0}
                          </Link>
                        </td>

                        {/* Persentase PT Mengisi */}
                        <td className="text-center" style={{ padding: "16px", borderTop: "1px solid #f1f5f9", borderBottom: "1px solid #f1f5f9", textAlign: "center", verticalAlign: "middle" }}>
                          <span style={{ fontWeight: 700, color: statusColor }}>{item.persentase_pt_mengisi}%</span>
                        </td>

                        {/* Persentase PT Mengisi Lengkap */}
                        <td className="text-center" style={{ padding: "16px", borderTop: "1px solid #f1f5f9", borderBottom: "1px solid #f1f5f9", textAlign: "center", verticalAlign: "middle" }}>
                          <span style={{ fontWeight: 700, color: item.persentase_pt_mengisi_lengkap >= 70 ? "#10b981" : "#f59e0b" }}>{item.persentase_pt_mengisi_lengkap}%</span>
                        </td>

                        {/* Persentase PT Terverifikasi Lengkap */}
                        <td className="text-center" style={{ padding: "16px", borderTop: "1px solid #f1f5f9", borderBottom: "1px solid #f1f5f9", textAlign: "center", verticalAlign: "middle" }}>
                          <span style={{ fontWeight: 700, color: item.persentase_pt_terverifikasi_lengkap >= 70 ? "#10b981" : "#f59e0b" }}>{item.persentase_pt_terverifikasi_lengkap}%</span>
                        </td>

                        {/* PT Terakreditasi */}
                        <td className="text-center" style={{ padding: "16px", fontWeight: 600, color: "#10b981", borderTop: "1px solid #f1f5f9", borderBottom: "1px solid #f1f5f9", textAlign: "center", verticalAlign: "middle" }}>
                          {item.pt_akreditasi || 0}
                        </td>

                        {/* PT Belum Terakreditasi */}
                        <td className="text-center" style={{ padding: "16px", fontWeight: 600, color: "#f59e0b", borderRadius: "0 5px 5px 0", borderTop: "1px solid #f1f5f9", borderBottom: "1px solid #f1f5f9", borderRight: "1px solid #f1f5f9", textAlign: "center", verticalAlign: "middle" }}>
                          {item.pt_tanpa_akreditasi || 0}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
}
